import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";

interface ScraperQuota {
  maxJobsPerDay: number;
  maxPagesPerJob: number;
  maxPagesPerMonth: number;
  jobsToday: number;
  pagesThisMonth: number;
  isVelaroAdmin: boolean;
  autoRebuildEnabled: boolean;
  rebuildIntervalDays: number;
  rebuildIntervalLabel: string;
  lastAutoRebuildAt: string | null;
  nextAutoRebuildAt: string | null;
}

function UsageBar({ current, max, label }: { current: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const color =
    pct >= 90 ? "bg-crimson-red-500" : pct >= 70 ? "bg-sweet-orange-400" : "bg-cornflower-blue-500";
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span>
        <span>
          {current.toLocaleString()} / {max > 0 ? max.toLocaleString() : "∞"}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ScraperLimitsPanel() {
  const api = useApi();
  const [quota, setQuota] = useState<ScraperQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ maxJobsPerDay: 0, maxPagesPerJob: 0, maxPagesPerMonth: 0 });
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState<string | null>(null);

  const fetchQuota = useCallback(async () => {
    try {
      const resp = await api.admin.get("ScraperJobs/Quota");
      if (resp.ok) {
        const data: ScraperQuota = await resp.json();
        setQuota(data);
        setForm({
          maxJobsPerDay: data.maxJobsPerDay,
          maxPagesPerJob: data.maxPagesPerJob,
          maxPagesPerMonth: data.maxPagesPerMonth,
        });
      } else {
        setError("Failed to load scraper quota.");
      }
    } catch (ex: unknown) {
      console.error("Failed to load scraper quota:", ex);
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchQuota(); }, [fetchQuota]);

  const handleTriggerNow = async () => {
    setTriggering(true);
    setTriggerMsg(null);
    try {
      const resp = await api.admin.post("ScraperJobs/TriggerNow", {});
      setTriggerMsg(resp.ok
        ? "Rebuild triggered — check the Knowledge Base tab for progress."
        : "Failed to trigger rebuild.");
    } catch (ex: unknown) {
      console.error("Failed to trigger scraper rebuild:", ex);
      setTriggerMsg("Failed to connect to server.");
    } finally {
      setTriggering(false);
      setTimeout(() => setTriggerMsg(null), 8000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await api.admin.put("ScraperJobs/Quota", form);
      if (resp.ok) {
        setEditing(false);
        await fetchQuota();
      } else {
        setError("Failed to save quota changes.");
      }
    } catch (ex: unknown) {
      console.error("Failed to save scraper quota:", ex);
      setError("Failed to connect to server.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-slate-400 animate-pulse">Loading scraper limits...</div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-crimson-red-600">{error}</div>;
  }

  if (!quota) return null;

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-gray-800">Web Scraper Limits</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Quotas enforced per site. Resets: jobs/pages-today at midnight UTC, pages/month on the
            1st.
          </p>
        </div>
        {quota.isVelaroAdmin && !editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleTriggerNow}
              disabled={triggering}
              className="text-xs px-3 py-1.5 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
            >
              {triggering ? "Triggering…" : "Trigger Rebuild Now"}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="text-xs px-3 py-1.5 rounded border border-cornflower-blue-300 text-cornflower-blue-600 hover:bg-cornflower-blue-50"
            >
              Edit Limits
            </button>
          </div>
        )}
        {triggerMsg && (
          <p className="text-xs text-green-700 mt-1">{triggerMsg}</p>
        )}
      </div>

      {!editing ? (
        <div className="space-y-4">
          <UsageBar
            label="Scraper jobs today"
            current={quota.jobsToday}
            max={quota.maxJobsPerDay}
          />
          <div className="flex justify-between text-xs text-slate-500">
            <span>Max pages per job</span>
            <span className="font-medium text-slate-700">{quota.maxPagesPerJob.toLocaleString()}</span>
          </div>
          <UsageBar
            label="Pages crawled this month"
            current={quota.pagesThisMonth}
            max={quota.maxPagesPerMonth}
          />
        </div>
      ) : (
        <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">
            Edit Scraper Limits
          </p>
          {[
            { key: "maxJobsPerDay", label: "Max jobs per day" },
            { key: "maxPagesPerJob", label: "Max pages per job" },
            { key: "maxPagesPerMonth", label: "Max pages per month" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3">
              <label className="w-48 text-sm text-slate-600 flex-shrink-0">{label}</label>
              <input
                type="number"
                min={1}
                value={form[key as keyof typeof form]}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [key]: parseInt(e.target.value) || 1 }))
                }
                className="w-32 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cornflower-blue-400"
              />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm bg-cornflower-blue-600 text-white rounded hover:bg-cornflower-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-xs text-crimson-red-600">{error}</p>}
        </div>
      )}

      {/* Auto-Refresh Schedule */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Knowledge Base Auto-Refresh</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            quota.autoRebuildEnabled
              ? "bg-green-100 text-green-700"
              : "bg-slate-200 text-slate-500"
          }`}>
            {quota.autoRebuildEnabled ? quota.rebuildIntervalLabel : "Manual only"}
          </span>
        </div>
        <p className="text-xs text-slate-500">
          {quota.autoRebuildEnabled
            ? `Your AI knowledge base is automatically re-crawled and re-indexed ${quota.rebuildIntervalLabel.toLowerCase()} to keep the bot's answers current.`
            : "Upgrade to Pro or Enterprise to enable automatic knowledge base refresh."}
        </p>
        {quota.autoRebuildEnabled && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="text-xs">
              <span className="text-slate-400">Last refresh</span>
              <div className="font-medium text-slate-700 mt-0.5">
                {quota.lastAutoRebuildAt
                  ? new Date(quota.lastAutoRebuildAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                  : "Not yet run"}
              </div>
            </div>
            <div className="text-xs">
              <span className="text-slate-400">Next refresh</span>
              <div className="font-medium text-slate-700 mt-0.5">
                {quota.nextAutoRebuildAt
                  ? new Date(quota.nextAutoRebuildAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                  : quota.lastAutoRebuildAt ? "Scheduled" : "After first manual crawl"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="text-xs text-slate-400 border-t pt-3 space-y-0.5">
        <p>
          <span className="font-medium">Default limits:</span> 3 jobs/day · 200 pages/job · 1,000
          pages/month
        </p>
        <p>Contact Velaro support to increase limits for your plan.</p>
      </div>
    </div>
  );
}
